import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  ArrowDownTrayIcon,
  ChartBarIcon,
  UsersIcon,
  ChartPieIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts';

import { listAssessments }                       from '../../api/assessmentApi';
import { getAssessmentReport, exportReport }     from '../../api/reportApi';
import Card       from '../../components/ui/Card';
import Spinner    from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';

// ── Helpers ───────────────────────────────────────────────────────────────────
function buildDistribution(rankedParticipants = []) {
  const buckets = [
    { range: '0–20',  min: 0,  max: 20,  count: 0 },
    { range: '20–40', min: 20, max: 40,  count: 0 },
    { range: '40–60', min: 40, max: 60,  count: 0 },
    { range: '60–80', min: 60, max: 80,  count: 0 },
    { range: '80–100',min: 80, max: 101, count: 0 },
  ];
  rankedParticipants.forEach((p) => {
    const s = p.overall_score ?? p.score ?? 0;
    const b = buckets.find((b) => s >= b.min && s < b.max);
    if (b) b.count++;
  });
  return buckets.map(({ range, count }) => ({ range, count }));
}

function buildRadarData(dimensionAverages = {}) {
  return Object.entries(dimensionAverages).map(([name, value]) => ({
    dimension: name,
    score:     Math.round(value),
    fullMark:  100,
  }));
}

function StatCard({ icon: Icon, label, value, sub, colorClass }) {
  return (
    <div className="flex items-center gap-4 rounded-card border border-neutral-200 bg-white px-5 py-4 shadow-card">
      <div
        className={clsx(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-full',
          colorClass
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-heading font-bold text-neutral-900">
          {value ?? '—'}
        </p>
        <p className="text-xs text-neutral-500">{label}</p>
        {sub && <p className="text-xs text-neutral-400">{sub}</p>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const [assessments, setAssessments]   = useState([]);
  const [selectedId, setSelectedId]     = useState('');
  const [report, setReport]             = useState(null);

  const [assLoading, setAssLoading]     = useState(true);
  const [repLoading, setRepLoading]     = useState(false);
  const [exporting, setExporting]       = useState(false);
  const [repError, setRepError]         = useState(null);

  // Load assessments list
  useEffect(() => {
    setAssLoading(true);
    listAssessments({ per_page: 100 })
      .then((res) => {
        const list = res.data.data ?? res.data ?? [];
        setAssessments(list);
        if (list.length > 0 && !selectedId) {
          setSelectedId(String(list[0].id));
        }
      })
      .catch(() => toast.error('Failed to load assessments'))
      .finally(() => setAssLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load report when assessment selected
  const fetchReport = useCallback(() => {
    if (!selectedId) return;
    setRepLoading(true);
    setRepError(null);
    setReport(null);
    getAssessmentReport(selectedId)
      .then((res) => setReport(res.data.data ?? res.data))
      .catch((err) => setRepError(err?.response?.data?.message ?? 'Failed to load report'))
      .finally(() => setRepLoading(false));
  }, [selectedId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleExport = async () => {
    if (!selectedId) return;
    setExporting(true);
    try {
      const res  = await exportReport(selectedId, 'csv');
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `report-${selectedId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Derived chart data
  const distributionData = report?.ranked_participants
    ? buildDistribution(report.ranked_participants)
    : report?.score_distribution ?? [];

  const radarData = report?.dimension_averages
    ? buildRadarData(report.dimension_averages)
    : [];

  const ranked = report?.ranked_participants ?? report?.top_participants ?? [];

  return (
    <div className="min-h-screen bg-surface p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Reports</h1>
          <p className="mt-0.5 text-sm text-neutral-500">
            Assessment performance analytics and insights
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || !selectedId || !report}
          className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
        >
          {exporting ? (
            <Spinner size="sm" className="text-white" />
          ) : (
            <ArrowDownTrayIcon className="h-4 w-4" />
          )}
          Export CSV
        </button>
      </div>

      {/* Assessment Selector */}
      <Card className="mb-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <label className="shrink-0 text-sm font-medium text-neutral-700">
            Assessment
          </label>
          {assLoading ? (
            <div className="flex items-center gap-2">
              <Spinner size="sm" className="text-neutral-400" />
              <span className="text-sm text-neutral-400">Loading...</span>
            </div>
          ) : (
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full rounded-input border border-neutral-200 px-3 py-2 text-sm focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:max-w-sm"
            >
              <option value="">— Select an assessment —</option>
              {assessments.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.title}
                </option>
              ))}
            </select>
          )}
        </div>
      </Card>

      {/* Report Content */}
      {!selectedId ? (
        <EmptyState
          icon={ChartBarIcon}
          title="Select an assessment"
          description="Choose an assessment above to view its report."
        />
      ) : repLoading ? (
        <div className="flex items-center justify-center py-24">
          <Spinner size="xl" className="text-primary-600" />
        </div>
      ) : repError ? (
        <div className="py-16 text-center">
          <p className="text-sm text-error-500">{repError}</p>
          <button
            onClick={fetchReport}
            className="mt-2 text-sm text-primary-600 underline"
          >
            Retry
          </button>
        </div>
      ) : !report ? null : (
        <div className="space-y-6 animate-fade-in">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              icon={UsersIcon}
              label="Total Participants"
              value={report.total_participants ?? ranked.length}
              colorClass="bg-primary-100 text-primary-600"
            />
            <StatCard
              icon={ChartBarIcon}
              label="Average Score"
              value={
                report.average_score != null
                  ? Number(report.average_score).toFixed(1)
                  : '—'
              }
              colorClass="bg-secondary-100 text-secondary-600"
            />
            <StatCard
              icon={ChartPieIcon}
              label="Completion Rate"
              value={
                report.completion_rate != null
                  ? `${Math.round(report.completion_rate)}%`
                  : '—'
              }
              colorClass="bg-accent-100 text-accent-600"
            />
            <StatCard
              icon={CheckBadgeIcon}
              label="Analyzed"
              value={report.analyzed_count ?? '—'}
              colorClass="bg-success-100 text-success-600"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Score Distribution Bar Chart */}
            <Card title="Score Distribution">
              {distributionData.length === 0 ? (
                <EmptyState
                  icon={ChartBarIcon}
                  title="No distribution data"
                  description="Submit and analyze more participants to see score distribution."
                  className="border-0 py-12"
                />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={distributionData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis
                      dataKey="range"
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0/0.1)',
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#6366F1"
                      radius={[4, 4, 0, 0]}
                      name="Participants"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Dimension Radar Chart */}
            <Card title="Dimension Averages">
              {radarData.length === 0 ? (
                <EmptyState
                  icon={ChartPieIcon}
                  title="No dimension data"
                  description="Dimension averages will appear once participants have been analyzed."
                  className="border-0 py-12"
                />
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fontSize: 10, fill: '#64748B' }}
                    />
                    <PolarRadiusAxis
                      angle={30}
                      domain={[0, 100]}
                      tick={{ fontSize: 9, fill: '#94A3B8' }}
                    />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#6366F1"
                      fill="#6366F1"
                      fillOpacity={0.25}
                      strokeWidth={2}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 8,
                        border: '1px solid #E2E8F0',
                        fontSize: 12,
                      }}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Top Participants Table */}
          <Card
            title="Top Participants"
            subtitle={ranked.length > 0 ? `${ranked.length} ranked` : undefined}
          >
            {ranked.length === 0 ? (
              <EmptyState
                icon={UsersIcon}
                title="No ranked participants"
                description="Participants with analysis results will appear here."
                className="border-0 py-12"
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 text-left">
                      {['Rank', 'Name', 'Startup', 'Score'].map((col) => (
                        <th
                          key={col}
                          className="pb-3 pr-4 text-xs font-semibold uppercase tracking-wide text-neutral-400 last:pr-0"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-50">
                    {ranked.map((p, idx) => {
                      const score = p.overall_score ?? p.score;
                      let scoreClass = 'text-neutral-700';
                      if (score >= 80)      scoreClass = 'text-success-600 font-semibold';
                      else if (score >= 60) scoreClass = 'text-secondary-600 font-semibold';
                      else if (score >= 40) scoreClass = 'text-accent-600 font-semibold';
                      else if (score != null) scoreClass = 'text-error-500 font-semibold';

                      return (
                        <tr key={p.id ?? idx} className="hover:bg-neutral-50 transition">
                          <td className="py-3 pr-4 font-mono text-xs text-neutral-400">
                            #{p.rank ?? idx + 1}
                          </td>
                          <td className="py-3 pr-4 font-medium text-neutral-900">
                            {p.full_name ?? p.name ?? '—'}
                          </td>
                          <td className="py-3 pr-4 text-neutral-600">
                            {p.startup_name ?? '—'}
                          </td>
                          <td className={clsx('py-3 font-heading', scoreClass)}>
                            {score != null ? score : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
